import { MILLISECONDS_IN_MINUTE } from '@stdlib/constants-time';
import { useQuery } from '@tanstack/react-query';

import { computePacks } from 'app/components/pages/Extranet/ExtranetInventory/Modern/utils/packs';
import { ChannelManager } from 'app/typings/channelManagers';
import type { ExtranetHotelExperience } from 'app/typings/core/extranet/hotel/experiences';
import type { ExtranetHotelPack } from 'app/typings/core/extranet/hotel/packs';
import type { InventoryResponse } from 'app/typings/core/extranet/inventory/inventory';
import type { Hotel } from 'app/typings/hotels/Hotel/Hotel';
import { apiRequest } from 'app/utils/request/api';

import { CORE_QUERY_KEYS, ENDPOINTS } from '.';

type TimePeriod = {
  startDate: string;
  endDate: string;
};

type ComputePacksParameters = {
  saleEndDateString: string;
  packData: ExtranetHotelPack[];
  experiences: ExtranetHotelExperience[];
  originals: ExtranetHotelExperience[];
};

export function useInventory(
  hotelInfo?: Hotel,
  options?: TimePeriod,
  computePacksParameters?: ComputePacksParameters,
  hasPublishedPacks?: boolean
) {
  const { data, isFetching } = useQuery<InventoryResponse | null>({
    queryKey: [
      CORE_QUERY_KEYS.inventory,
      { hotelId: hotelInfo?.id, ...options },
    ],
    queryFn: () =>
      hotelInfo?.id
        ? (apiRequest({
            path: ENDPOINTS.getInventory,
            params: { hotelId: hotelInfo.id.toString() },
            queryParams: {
              startDate: options?.startDate,
              endDate: options?.endDate,
            },
          }) as Promise<InventoryResponse>)
        : Promise.resolve(null),
    staleTime: 5 * MILLISECONDS_IN_MINUTE,
    enabled: !!hotelInfo?.id,
  });

  if (computePacksParameters && options && data) {
    return {
      inventory: computePacks(
        computePacksParameters?.saleEndDateString,
        options?.startDate,
        options?.endDate,
        computePacksParameters?.packData,
        computePacksParameters?.experiences,
        computePacksParameters?.originals,
        data ?? {},
        !!hotelInfo?.channelManager &&
          hotelInfo?.channelManager !== ChannelManager.NONE,
        !!hotelInfo?.stockScrapperEnabled
      ),
      rawInventory: data,
      isFetching,
    };
  }

  return { inventory: undefined, rawInventory: data, isFetching };
}
